import React, { type ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

export class FlowErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[FlowErrorBoundary] Caught runtime error in Luna Flow Builder:', error, errorInfo);
    this.setState({ errorInfo });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center h-full min-h-[480px] w-full bg-slate-950/80 backdrop-blur-2xl rounded-3xl border border-rose-500/30 p-8 text-center shadow-2xl">
          <div className="p-4 rounded-2xl bg-rose-500/20 border border-rose-500/40 text-rose-400 mb-4 shadow-[0_0_30px_rgba(244,63,94,0.3)]">
            <AlertTriangle size={36} className="animate-pulse" />
          </div>
          <h3 className="text-lg font-black text-white uppercase tracking-wider mb-2 font-sans">
            Luna Flow Canvas Diagnostic Intercept
          </h3>
          <p className="text-xs text-rose-300/90 max-w-md mb-4 font-mono bg-rose-950/40 px-4 py-2 rounded-xl border border-rose-500/20 break-all">
            {this.state.error?.message || 'Unknown runtime render error in Flow Canvas.'}
          </p>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={this.handleReset}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-rose-500 to-amber-500 hover:from-rose-400 hover:to-amber-400 text-slate-950 font-black text-xs uppercase tracking-wider transition-all shadow-lg active:scale-95 cursor-pointer"
            >
              <RefreshCw size={14} /> Reset & Reload Flow Canvas
            </button>
          </div>
          {this.state.errorInfo?.componentStack && (
            <details className="mt-6 text-left max-w-xl w-full bg-black/60 border border-white/10 rounded-xl p-3 text-[10px] text-slate-400 font-mono overflow-auto max-h-40">
              <summary className="cursor-pointer text-slate-300 font-bold mb-1">Component Stack Trace</summary>
              <pre className="whitespace-pre-wrap">{this.state.errorInfo.componentStack}</pre>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export default FlowErrorBoundary;
